import React, { useState, useEffect } from 'react';
import { devLog, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { AppTextField } from '../../customComponents/AppTextField';
import { AppDropdownField } from '../../customComponents/AppDropdownField';
import { AppGPhoneTextField } from '../../customComponents/AppGPhoneTextField';
import { FormTitle } from '../../customComponents/FormTitle';
import PrimaryButton from '../../components/PrimaryButton';
import Agreement from '../../components/Agreement';
import TermsConsentSheet from '../../components/TermsConsentSheet';
import CenteredLayout from '../../layout/CenteredLayout';
import PageContainerLayout from '../../layout/PageContainerLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import FormContainer from '../../customComponents/FormContainer';
import { updateUserInfo, getTerms } from '../../lib/api/user/userApi';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLang } from '../../context/LangContext';

// 다국어 텍스트 객체
const additionalInfoTexts = {
  ko: {
    title: '추가 정보 입력',
    subtitle: '서비스 이용을위해 추가정보를 입력해주세요',
    updateSuccess: '추가 정보가 저장되었습니다!',
    updateFailed: '정보 저장에 실패했습니다. 다시 시도해주세요.',
    termsLoadFailed: '약관을 불러오는데 실패했습니다.',
    nameValidationError: '이름은 2글자 이상 입력해주세요',
    countryValidationError: '국가를 선택해주세요',
    completeButton: '완료',
    requiredMessage: '추가 정보 입력이 필요합니다'
  },
  en: {
    title: 'Additional Information',
    subtitle: 'Please enter additional information to use the service',
    updateSuccess: 'Additional information saved successfully!',
    updateFailed: 'Failed to save information. Please try again.',
    termsLoadFailed: 'Failed to load terms.',
    nameValidationError: 'Please enter at least 2 characters for name',
    countryValidationError: 'Please select country',
    completeButton: 'Complete',
    requiredMessage: 'Additional information input is required'
  }
};

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubtitleText = styled.p`
  font-size: 16px;
  color: #666;
  text-align: center;
  margin: 0 0 24px 0;
  line-height: 1.4;
`;

const CompleteButtonContainer = styled.div`
  width: 100%;
  margin-top: 24px;
`;

function AdditionalInfo() {
  const navigate = useNavigate();
  const { user, login, logout } = useUserAuth();
  const { lang } = useLang();
  const texts = additionalInfoTexts[lang];

  // 로그인되지 않았거나 추가 정보가 이미 입력된 경우 홈으로 리다이렉트
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // needMoreInfo가 false인 경우 (추가 정보가 이미 입력됨) 홈으로 리다이렉트
    if (!user.needMoreInfo) {
      navigate('/');
      return;
    }
  }, [user, navigate]);
  
  const [name, setName] = useState(''); 
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [isNameValid, setIsNameValid] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isCountryValid, setIsCountryValid] = useState(false);
  const [individualAgreements, setIndividualAgreements] = useState<boolean[]>([false, false]);
  const [isTermsSheetOpen, setIsTermsSheetOpen] = useState(false);
  const [currentTermsSection, setCurrentTermsSection] = useState('privacy');
  const [termsSection, setTermsSection] = useState<any[]>([]);
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Context에서 받은 사용자 정보로 이름 설정
  useEffect(() => {
    if (user?.name && user.name.trim() !== '') {
      setName(user.name);
      setIsNameValid(user.name.trim().length >= 2);
      devLog('Context에서 이름 정보 가져옴:', user.name);
    } else {
      devLog('Context에 이름 정보가 없음');
    }
  }, [user]);

  // 약관 데이터 불러오기
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setIsLoadingTerms(true);
        const termLang = lang === 'ko' ? 'KOR' : 'ENG';
        const response = await getTerms({ termLang });
        
        if (response.data.statusCode === 200 && response.data.data) {
          const termsArray = response.data.data as any[];
          
          // 언어에 따라 인덱스 매핑
          let privacyIndex, serviceIndex;
          if (lang === 'ko') {
            // KOR: 1(privacy), 3(service)
            privacyIndex = termsArray.find((term: any) => term.index === 1);
            serviceIndex = termsArray.find((term: any) => term.index === 3);
          } else {
            // ENG: 2(privacy), 4(service)
            privacyIndex = termsArray.find((term: any) => term.index === 2);
            serviceIndex = termsArray.find((term: any) => term.index === 4);
          }

          const formattedTerms = [
            {
              key: 'privacy',
              title: lang === 'ko' ? '개인정보\n취급방침' : 'Privacy\nPolicy',
              content: privacyIndex?.content || ''
            },
            {
              key: 'terms',
              title: lang === 'ko' ? '서비스\n이용약관' : 'Terms of\nService',
              content: serviceIndex?.content || ''
            }
          ];

          setTermsSection(formattedTerms);
        }
      } catch (error) {
        devError('약관 조회 실패:', error);
        toast.error(texts.termsLoadFailed);
      } finally {
        setIsLoadingTerms(false);
      }
    };

    fetchTerms();
  }, [lang, texts.termsLoadFailed]);

  const countryOptions = [
    { value: 'domestic', label: lang === 'ko' ? '국내' : 'Domestic' },
    { value: 'overseas', label: lang === 'ko' ? '국외' : 'Overseas' },
  ];

  const handleBack = () => {
    // 추가 정보 입력이 필수이므로 로그아웃 후 로그인 페이지로 이동
    logout();
    navigate('/login');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setName(e.target.value);
    setIsNameValid(e.target.value.trim().length >= 2);
  };

  const handlePhoneChange = (value: string, isValid: boolean) => {
    setPhone(value);
    setIsPhoneValid(isValid);
  };

  const handleCountryChange = (value: string) => {
    setCountry(value);
    setIsCountryValid(value !== '');
  };

  const handleAgreementChange = (allAgreed: boolean, agreements: boolean[]) => {
    setIndividualAgreements(agreements);
  };

  const handleViewPrivacyPolicy = () => {
    setCurrentTermsSection('privacy');
    setIsTermsSheetOpen(true);
  };

  const handleViewTerms = () => {
    setCurrentTermsSection('terms');
    setIsTermsSheetOpen(true);
  };

  const handleTermsSheetClose = () => {
    setIsTermsSheetOpen(false);
  };

  const handleTermsSectionClick = (sectionKey: string) => {
    setCurrentTermsSection(sectionKey);
  };

  const handleComplete = async () => {
    if (isLoading || !user) return;

    try {
      setIsLoading(true);
      devLog('추가 정보 업데이트 시작:', { 
        name, 
        phone, 
        country,
        userId: user.userId
      });

      // 추가 정보 업데이트 API 호출 (API 엔드포인트는 서버에 맞게 수정 필요)
      const response = await updateUserInfo({
        name: name.trim(),
        cellphone: phone,
        locationType: country === 'domestic' ? 'DOMESTIC' : 'OVERSEAS'
      });

      if (response.data.statusCode === 200) {
        toast.success(texts.updateSuccess);
        
        // 사용자 정보 업데이트 (needMoreInfo를 false로 변경)
        const updatedUser = {
          ...user,
          name: name.trim(),
          needMoreInfo: false,
          locationType: country === 'domestic' ? 'DOMESTIC' : 'OVERSEAS'
        };
        
        // Context의 사용자 정보 업데이트
        login(updatedUser, localStorage.getItem('accessToken') || '');
        
        // 홈 페이지로 이동
        navigate('/');
      } else {
        toast.error(texts.updateFailed);
      }
    } catch (error: any) {
      devError('추가 정보 업데이트 실패:', error);
      devError('에러 상세 정보:', {
        status: error?.status,
        data: error?.data,
        message: error?.message,
        response: error?.response
      });
      
      // 토큰 관련 에러인지 확인
      if (error?.status === 401 || error?.data?.statusCode === 401) {
        devError('🔐 토큰 만료 또는 인증 실패 에러 감지');
        devError('현재 저장된 토큰:', {
          accessToken: localStorage.getItem('accessToken')?.substring(0, 20) + '...',
          hasToken: !!localStorage.getItem('accessToken')
        });
      }
      
      // 에러 메시지 파싱
      let errorMessage = texts.updateFailed;
      
      if (error?.data?.error?.customMessage) {
        errorMessage = error.data.error.customMessage;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = isNameValid && isPhoneValid && isCountryValid && 
                     individualAgreements.every(agreed => agreed) &&
                     name.trim() !== '' && phone.trim() !== '' && country.trim() !== '';

  const agreeContents = [
    {
      title: lang === 'ko' 
        ? '(필수) 개인정보 취급방침 동의' 
        : '(Required) Privacy Policy Agreement',
      onView: handleViewPrivacyPolicy
    },
    {
      title: lang === 'ko' 
        ? '(필수) 서비스 이용약관 수집 동의' 
        : '(Required) Terms of Service Agreement',
      onView: handleViewTerms
    }
  ];

  return (
    <CenteredLayout>
      <ProjectAppbar 
        title={texts.title}
        showBackButton={true}
        onBack={handleBack}
      />
      <FormContainer>
        <PageContainerLayout>
          <ContentContainer>
            <SubtitleText>{texts.subtitle}</SubtitleText>
            
            <FormGroup>
              <FormTitle>{lang === 'ko' ? "회원정보 입력" : "Enter Member Information"}</FormTitle>
              
              <AppTextField
                value={name}
                onChange={handleNameChange}
                label={lang === 'ko' ? "이름" : "Name"}
                placeholder={lang === 'ko' ? "이름을 입력해주세요" : "Please enter your name"}
                errorMessage={name && !isNameValid ? texts.nameValidationError : ''}
                autoFocus={!name}
                autoComplete="name"
              />

              <AppGPhoneTextField
                value={phone}
                onChange={handlePhoneChange}
                language={lang}
              />

              <AppDropdownField
                value={country}
                onChange={handleCountryChange}
                options={countryOptions}
                label={lang === 'ko' ? "국가" : "Country"}
                placeholder={lang === 'ko' ? "국가를 선택해주세요" : "Please select country"}
                errorMessage={country && !isCountryValid ? texts.countryValidationError : ''}
                language={lang}
              />
            </FormGroup>

            <Agreement
              agreeContents={agreeContents}
              onAgreementChange={handleAgreementChange}
              initialAgreements={individualAgreements}
            />
          </ContentContainer>

          <CompleteButtonContainer>
            <PrimaryButton
              onClick={handleComplete}
              disabled={!isFormValid || isLoading}
            >
              {isLoading ? '저장 중...' : texts.completeButton}
            </PrimaryButton>
          </CompleteButtonContainer>
        </PageContainerLayout>

      {isTermsSheetOpen && (
        <TermsConsentSheet
          sections={termsSection}
          viewedSection={currentTermsSection}
          onClose={handleTermsSheetClose}
          onSectionClick={handleTermsSectionClick}
          isLoading={isLoadingTerms}
        />
      )}
      </FormContainer>
    </CenteredLayout>
  );
}

export default AdditionalInfo;
